/* assets/product-reviews-carousel.js
 * Fetches reviews from the Vercel proxy and renders a Flickity carousel using
 * the designer's review-card layout (matches snippets/review-card.liquid output).
 * Degrades to a native scroll-snap row if Flickity isn't present.
 */
(function () {
  'use strict';

  var hydrated = new WeakSet();

  // Star glyph from the designer's star-rating snippet (viewBox 0 0 35 34).
  var STAR_PATH =
    'M17.2694 0.138206C17.3293 -0.0460565 17.59 -0.0460565 17.6498 0.138206L21.6344 12.4013C21.6611 12.4837 21.7379 12.5395 21.8246 12.5395H34.7188C34.9125 12.5395 34.9931 12.7874 34.8363 12.9013L24.4047 20.4804C24.3346 20.5313 24.3053 20.6216 24.332 20.704L28.3166 32.9671C28.3764 33.1514 28.1656 33.3046 28.0088 33.1907L17.5772 25.6117C17.5071 25.5607 17.4122 25.5607 17.3421 25.6117L6.91042 33.1907C6.75368 33.3046 6.54278 33.1514 6.60265 32.9671L10.5872 20.704C10.614 20.6216 10.5846 20.5313 10.5145 20.4804L0.082891 12.9013C-0.0738517 12.7874 0.00670326 12.5395 0.200448 12.5395H13.0947C13.1813 12.5395 13.2581 12.4837 13.2849 12.4013L17.2694 0.138206Z';

  var STAR_FILLED = '#00c281';
  var STAR_EMPTY = '#d9d9d9';

  /* ---------- helpers ---------- */

  function escapeHTML(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function starSVG(fill) {
    return (
      '<svg width="28" height="27" viewBox="0 0 35 34" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="' + STAR_PATH + '" fill="' + fill + '"></path>' +
      '</svg>'
    );
  }

  function renderStars(rating) {
    var full = Math.round(rating || 0);
    var svgs = '';
    for (var i = 1; i <= 5; i++) {
      svgs += starSVG(i <= full ? STAR_FILLED : STAR_EMPTY);
    }
    return (
      '<div class="review-card-stars-klv" role="img" aria-label="' + (rating || 0) + '/5">' +
      '<div class="star-rating-block" style="text-decoration:none;display:inline-flex;flex-direction:column;align-items:center;gap:8px;">' +
      '<div class="star-rating-block__stars" style="display:flex;gap:6px;align-items:center;">' +
      svgs +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  /* Designer card markup (mirrors review-card.liquid, fixed "klv" suffix). */
  function card(review, index, stagger) {
    var stg = stagger ? ' fr-stagger-' + (index % 6) : '';
    return (
      '<div class="flip-reviews__cell' + stg + '">' +
      '<div class="review-card-klv">' +
      '<div class="review-card-content-klv">' +
      renderStars(review.rating) +
      '<div class="review-card-quote-klv">' + escapeHTML(review.content) + '</div>' +
      '<div class="review-card-reviewer-klv">' + escapeHTML(review.author) + '</div>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  function buildSummary(data, i18n) {
    if (!data.count) return '';
    var avg = data.average != null ? data.average.toFixed(1) : '';
    var label = (i18n.summary || '%{count} reviews').replace('%{count}', data.count);
    return (
      (avg ? '<span class="flip-reviews__avg">' + avg + ' \u2605</span> \u00b7 ' : '') +
      '<span class="flip-reviews__count">' + escapeHTML(label) + '</span>'
    );
  }

  function whenFlickity(cb, tries) {
    tries = tries == null ? 40 : tries; // ~2s max
    if (window.Flickity) return cb(window.Flickity);
    if (tries <= 0) return cb(null); // give up; scroll-snap fallback takes over
    setTimeout(function () {
      whenFlickity(cb, tries - 1);
    }, 50);
  }

  /* ---------- lifecycle ---------- */

  function hide(root) {
    root.hidden = true;
  }

  function teardown(root) {
    if (root._flkty) {
      try {
        root._flkty.destroy();
      } catch (e) {}
      root._flkty = null;
    }
    hydrated.delete(root);
  }

  function hydrate(root) {
    if (hydrated.has(root)) return;
    hydrated.add(root);

    var productId = root.getAttribute('data-product-id');
    var proxy = (root.getAttribute('data-proxy-url') || '').replace(/\/+$/, '');
    var minRating = parseInt(root.getAttribute('data-min-rating') || '0', 10);
    var showArrows = root.getAttribute('data-arrows') !== 'false';
    var autoRaw = root.getAttribute('data-autoplay') || 'false';
    var autoPlay = autoRaw === 'false' ? false : parseInt(autoRaw, 10) || false;
    var stagger = root.getAttribute('data-stagger') !== 'false';

    var statusEl = root.querySelector('[data-reviews-status]');
    var trackEl = root.querySelector('[data-reviews-track]');
    var summaryEl = root.querySelector('[data-reviews-summary]');

    var i18n = {};
    var i18nEl = root.querySelector('[data-reviews-i18n]');
    if (i18nEl) {
      try {
        i18n = JSON.parse(i18nEl.textContent);
      } catch (e) {}
    }

    if (!productId || !proxy) {
      hide(root);
      return;
    }

    fetch(proxy + '/api/reviews?product_id=' + encodeURIComponent(productId), {
      headers: { accept: 'application/json' },
    })
      .then(function (r) {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.json();
      })
      .then(function (data) {
        // Summary reflects ALL published reviews, independent of the display filter.
        if (summaryEl && data.count) {
          summaryEl.innerHTML = buildSummary(data, i18n);
          summaryEl.hidden = false;
        }

        var reviews = (data.reviews || []).filter(function (rv) {
          return !minRating || (rv.rating || 0) >= minRating;
        });

        if (!reviews.length) {
          hide(root);
          return;
        }

        trackEl.innerHTML = reviews
          .map(function (rv, i) {
            return card(rv, i, stagger);
          })
          .join('');

        if (statusEl) statusEl.hidden = true;
        trackEl.hidden = false;

        whenFlickity(function (Flickity) {
          if (Flickity) {
            root._flkty = new Flickity(trackEl, {
              cellAlign: 'left',
              contain: true,
              pageDots: false,
              prevNextButtons: showArrows,
              groupCells: true,
              //wrapAround: autoPlay ? true : false,
              wrapAround: true,
              autoPlay: autoPlay,
              adaptiveHeight: false,
            });
          }
        });
      })
      .catch(function () {
        hide(root);
      });
  }

  function hydrateAll(scope) {
    (scope || document).querySelectorAll('[data-flip-reviews]').forEach(hydrate);
  }

  /* ---------- boot + Shopify soft navigation ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      hydrateAll();
    });
  } else {
    hydrateAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    hydrateAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-flip-reviews]').forEach(teardown);
  });

  document.addEventListener('page:load', function () {
    hydrateAll();
  });
  document.addEventListener('page:loaded', function () {
    hydrateAll();
  });
})();
